import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import intl from "react-intl-universal";
import { TbBuildingBank } from "react-icons/tb";
import { useHistory } from "react-router-dom";
import { useBaasInstance } from "../../../../../Contexts/BaasInstanceContext";
import { useBaasCountryOptions } from "../../../../../Hooks/useBaasCountryOptions";
import {
  BAAS_COUNTRY_CODES,
  BAAS_EMPLOYMENT_STATUSES,
  BAAS_END_CUSTOMER_TYPES,
  BAAS_ENTIDADE_WIZARD_STEPS,
  BAAS_OWNER_ROLES,
  BAAS_PAYMENT_VOLUMES,
  BAAS_PEP_STATUSES,
  BAAS_PURPOSE_OF_ACCOUNT,
  BAAS_SOURCE_OF_INCOME,
  buildBusinessAccountPayload,
  buildBusinessEntityContactPatch,
  createBusinessAccount,
  createEmptyBeneficialOwner,
  createInitialBusinessCreateForm,
  formatBusinessDocumentInput,
  getBusinessLookupDocumentDigits,
  getDefaultBusinessAccountPayloadJson,
  getIndustryRequirements,
  getNextWizardStepIndex,
  getPreviousWizardStepIndex,
  isFieldRequiredForIndustry,
  isOwnerFieldRequired,
  isStepOptionalForIndustry,
  isStepSkippedForIndustry,
  isValidBusinessCnpj,
  isExistingBusinessOwnerRow,
  isLastRequiredBeneficialOwner,
  mapBeneficialOwnerToApiPayload,
  mapPersonEntityToBeneficialOwner,
  applySerproBusinessDraftToForm,
  MAX_BENEFICIAL_OWNERS,
  ownerHasLinkedPerson,
  parseBusinessAccountPayloadJson,
  selectNewBeneficialOwnersForSave,
  validateBusinessFormForSubmit,
  validateBusinessFormStep,
} from "../../../../../Services/baasBusinessAccountApi";
import { buildPhoneDialOptions, formatPhonePartsForDisplay, mapPhonePartsFromPayload } from "../../../../../Services/baasPhoneParts";
import {
  getVisibleFields,
  hasAnyCompleteOwnerIdentityDoc,
  hasAnyOwnerIdentityDocNumber,
  hasIdentityDocData,
  isBrCountry,
  isUsCountry,
  clearEntidadeDraft,
  saveEntidadeDraft,
} from "../baasEntidadeCreateVisibility";
import { addBaasEntityBeneficialOwners, autocompleteBaasEntity, fetchBaasEntityById, lookupBusinessEntityByDocument, updateBaasEntityContact } from "../../../../../Services/baasEntidadesApi";
import { fetchBaasInstanceFeatures } from "../../../../../Services/baasInstancesApi";
import { formatBaasCreateConflictMessage } from "../../../../../Services/baasCreateConflictMessage";
import {
  formatBaasEntitySaveError,
  formatBaasEntityValidationMessage,
  getBeneficialOwnerIndexesFromError,
} from "../../../../../Services/baasEntityValidationMessage";
import {
  getDefaultLegalTypeForCountry,
  getLegalTypesForCountry,
  getPaymentRailsForCountry,
  normalizePaymentRails,
} from "../entityCompanyTypes";
import { evaluateEntityProcessingRisk } from "../entityProcessingRisk";
import {
  AddressFieldsGrid,
  BrAddressFieldsGrid,
  CheckboxField,
  CollapsibleSection,
  ConditionalHelper,
  CountrySelectField,
  DebugConsoleFooter,
  Field,
  getIndustryOptionLabel,
  IdentityDocFields,
  IndustryRiskBadge,
  IndustrySelectField,
  MultiCheckboxField,
  EntityHighRiskBanner,
  OptionalSectionNotice,
  OwnerParticipationFlags,
  OwnerReviewCard,
  OwnerSelectorBar,
  OwnershipTotalBanner,
  PersonConsoleLink,
  PhonePartsFields,
  ReviewSection,
  SectionHeading,
  StateOfIncorporationField,
  StepHeader,
  WizardStepTabs,
  inputClass,
  textareaClass,
} from "./baasEntidadeCreateFields";
import { BaasEntidadeDocumentsPanel } from "./BaasEntidadeDocumentsPanel";
import { BaasExistingPersonPickerModal } from "./BaasExistingPersonPickerModal";

export const BAAS_ENTIDADE_CREATE_STEPS = BAAS_ENTIDADE_WIZARD_STEPS;

const buildBusinessEntityDetailPath = (entityId) =>
  `/baas/entidades/${encodeURIComponent(entityId)}`;

const formAlertClass =
  "mt-4 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm font-medium text-red-800 dark:border-red-400/50 dark:bg-red-950/40 dark:text-red-100";

const lookupBannerClass = (tone) => {
  if (tone === "success") {
    return "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-200";
  }
  if (tone === "warn") {
    return "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100";
  }
  return "border-gray-200 bg-gray-50 text-gray-700 dark:border-brand-dark-border dark:bg-brand-dark-surface dark:text-gray-200";
};

const LookupResultBanner = ({ notice, tone, existingEntityId, onOpenEntity }) => {
  if (!notice) {
    return null;
  }

  return (
    <div className={`rounded-xl border px-4 py-3 text-sm ${lookupBannerClass(tone)}`} role="status">
      <p className="font-medium">{notice}</p>
      {existingEntityId ? (
        <button type="button" className="btn-outline mt-3" onClick={onOpenEntity}>
          {intl.get("baas.entidades.create.lookup.openEntity")}
        </button>
      ) : null}
    </div>
  );
};

const LookupSkeleton = () => (
  <div
    className="space-y-3 rounded-xl border border-gray-100 bg-gray-50/80 p-4 dark:border-brand-dark-border dark:bg-brand-dark-surface"
    aria-busy="true"
    aria-live="polite"
  >
    <p className="text-sm text-gray-600 dark:text-gray-300">
      {intl.get("baas.entidades.create.lookup.lookingUp")}
    </p>
    <div className="h-3 w-full animate-pulse rounded bg-gray-200 dark:bg-brand-dark-elevated" />
    <div className="h-3 w-4/5 animate-pulse rounded bg-gray-200 dark:bg-brand-dark-elevated" />
    <div className="h-3 w-2/3 animate-pulse rounded bg-gray-200 dark:bg-brand-dark-elevated" />
  </div>
);

const resolveEntityCreateConflictMessage = (error, fallbackKey) => {
  const validationMessage = formatBaasEntityValidationMessage(error);
  if (validationMessage) {
    return validationMessage;
  }

  const code = String(error?.code || error?.data?.error || "").toLowerCase();
  const status = error?.status ?? error?.data?.status;
  const isConflict =
    status === 409 ||
    code === "entity_already_exists" ||
    code === "duplicate_request";

  if (!isConflict) {
    return intl.get(fallbackKey);
  }

  const fieldErrors = error?.errors || error?.data?.errors;
  const fields =
    fieldErrors && typeof fieldErrors === "object" ? Object.keys(fieldErrors) : [];

  if (fields.length > 0) {
    const fieldLabels = fields
      .map((field) => {
        const key = `baas.entidades.conflict.field.${field}`;
        const label = intl.get(key);
        return label && label !== key ? label : field;
      })
      .join(", ");
    return intl.get("baas.entidades.conflict.alreadyExistsWithFields", { fields: fieldLabels });
  }

  const localizedDetail = formatBaasCreateConflictMessage(
    error,
    intl.get("baas.entidades.conflict.alreadyExists"),
  );
  return localizedDetail || intl.get("baas.entidades.conflict.alreadyExists");
};

const normalizeDraftForm = (form) => {
  if (!form) {
    return createInitialBusinessCreateForm();
  }

  const purposeOfAccount = Array.isArray(form.purposeOfAccount)
    ? form.purposeOfAccount
    : typeof form.purposeOfAccount === "string" && form.purposeOfAccount.trim()
      ? form.purposeOfAccount
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
      : [];

  const country = form.countryOfIncorporation ?? "";
  const legalTypes = getLegalTypesForCountry(country);
  const legalType = legalTypes.includes(form.legalType)
    ? form.legalType
    : getDefaultLegalTypeForCountry(country);

  const beneficialOwners = Array.isArray(form.beneficialOwners)
    ? form.beneficialOwners.map((owner) => ({
        ...createEmptyBeneficialOwner(),
        ...owner,
        phone: mapPhonePartsFromPayload({
          phone: owner?.phone,
          phone_number: typeof owner?.phone === "string" ? owner.phone : undefined,
        }),
        address: { ...createEmptyBeneficialOwner().address, ...(owner?.address ?? {}) },
        passport: { ...createEmptyBeneficialOwner().passport, ...(owner?.passport ?? {}) },
        driversLicense: {
          ...createEmptyBeneficialOwner().driversLicense,
          ...(owner?.driversLicense ?? {}),
        },
        nationalId: { ...createEmptyBeneficialOwner().nationalId, ...(owner?.nationalId ?? {}) },
      }))
    : [createEmptyBeneficialOwner()];

  return {
    ...createInitialBusinessCreateForm(),
    ...form,
    legalType,
    purposeOfAccount,
    expectedPaymentRails: normalizePaymentRails(form.expectedPaymentRails),
    email: typeof form.email === "string" ? form.email : "",
    phone: mapPhonePartsFromPayload({
      phone: form?.phone,
      phone_number: typeof form?.phone === "string" ? form.phone : undefined,
    }),
    beneficialOwners,
  };
};

const formatAddress = (address) => {
  const line2 = address.line2 ? `, ${address.line2}` : "";
  return `${address.line1}${line2}, ${address.city}, ${address.state} ${address.postalCode} · ${address.countryCode}`;
};

export const BaasEntidadeCreateForm = ({
  active = true,
  debugConsoleExpanded = false,
  variant = "full",
  showHeader = false,
  onCancel,
  onCreated,
  onUpdated,
  cancelLabel,
  readOnly = false,
  editMode = false,
  unlockIdentity = false,
  entityId = null,
  initialForm = null,
  fixedInstanceId = null,
  documents = null,
  documentsUploadEnabled = false,
  allowSkipKyc = null,
  onDocumentsUploaded,
  onDocumentUpdated,
  verificationEntity = null,
  hideDocumentsPanel = false,
}) => {
  const history = useHistory();
  const { instances, selectedInstance } = useBaasInstance();
  const [stepIndex, setStepIndex] = useState(0);
  const [activeOwnerIndex, setActiveOwnerIndex] = useState(0);
  const [instanceId, setInstanceId] = useState("");
  const activeInstance =
    instances.find((item) => item.id === instanceId) || selectedInstance || instances[0];
  const { countryOptions } = useBaasCountryOptions(activeInstance);
  const phoneDialOptions = buildPhoneDialOptions(countryOptions);
  const [form, setForm] = useState(createInitialBusinessCreateForm);
  const [debugExpanded, setDebugExpanded] = useState(debugConsoleExpanded);
  const [jsonText, setJsonText] = useState("");
  const [debugError, setDebugError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [validationIssues, setValidationIssues] = useState([]);
  const [saveSuccess, setSaveSuccess] = useState("");
  const [personPickerOpen, setPersonPickerOpen] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupNotice, setLookupNotice] = useState("");
  const [lookupNoticeTone, setLookupNoticeTone] = useState("info");
  const [existingEntityId, setExistingEntityId] = useState(null);
  const [ownerFieldError, setOwnerFieldError] = useState("");
  const [serproAutocompleteEnabled, setSerproAutocompleteEnabled] = useState(false);
  const serproLastDocRef = useRef("");
  const serproInFlightRef = useRef(false);

  const isViewOnly = readOnly && !editMode;
  const isEditableDetail = Boolean(editMode);
  const identityLocked = isViewOnly || (isEditableDetail && !unlockIdentity);
  const isCreateFlow = !readOnly && !editMode;

  useEffect(() => {
    let cancelled = false;
    const instance =
      instances.find((item) => item.id === instanceId) || selectedInstance || activeInstance;
    if (!instance?.baasInstanceId || !isCreateFlow) {
      setSerproAutocompleteEnabled(false);
      return undefined;
    }
    fetchBaasInstanceFeatures(instance)
      .then((features) => {
        if (cancelled) return;
        const list = Array.isArray(features) ? features : [];
        const row = list.find(
          (item) =>
            String(item?.featureCode || "")
              .trim()
              .toLowerCase() === "serpro.autocomplete",
        );
        // Absent row = never toggled → treat as off. Prefer explicit enabled=true.
        setSerproAutocompleteEnabled(Boolean(row?.enabled));
      })
      .catch(() => {
        if (!cancelled) setSerproAutocompleteEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [instanceId, selectedInstance, activeInstance, instances, isCreateFlow]);

  const applySerproDraft = useCallback((draft, warnings = []) => {
    if (!draft) return false;
    setForm((current) => applySerproBusinessDraftToForm(current, draft));
    setActiveOwnerIndex(0);
    const owners = draft?.business_details?.beneficial_owners
      ?? draft?.businessDetails?.beneficialOwners
      ?? draft?.beneficial_owners
      ?? [];
    const ownerCount = Array.isArray(owners) ? owners.length : 0;
    const warningList = Array.isArray(warnings) ? warnings : [];
    const noAdmins = warningList.some((w) => String(w).includes("no_administrador_pf"));
    if (ownerCount > 0) {
      setLookupNotice(
        intl.get("baas.entidades.create.lookup.serproFilledOwners", { count: ownerCount })
          || `SERPRO: empresa + ${ownerCount} administrador(es) preenchidos. Complete nascimento, mãe, e-mail, telefone, endereço e % de participação.`,
      );
      setLookupNoticeTone("success");
    } else if (noAdmins) {
      setLookupNotice(
        intl.get("baas.entidades.create.lookup.serproNoAdmins")
          || "SERPRO preencheu a empresa, mas não há sócio PF com qualificação Administrador (05). Inclua os administradores manualmente.",
      );
      setLookupNoticeTone("warn");
    } else {
      setLookupNotice(
        intl.get("baas.entidades.create.lookup.serproFilled")
          || "Dados preenchidos via SERPRO. Continue para revisar razão social, endereço e administradores.",
      );
      setLookupNoticeTone("success");
    }
    return true;
  }, []);

  const trySerproAutocomplete = useCallback(
    async (instance, digits, { force = false } = {}) => {
      if (!instance || !digits || digits.length !== 14) return false;
      if (!force && serproLastDocRef.current === digits) return Boolean(serproLastDocRef.current);
      if (serproInFlightRef.current) return false;

      serproInFlightRef.current = true;
      try {
        const auto = await autocompleteBaasEntity(instance, { document: digits });
        serproLastDocRef.current = digits;
        const draft = auto?.draft ?? auto?.data?.draft ?? null;
        if (draft) {
          applySerproDraft(draft, auto?.warnings ?? auto?.data?.warnings ?? []);
          setSerproAutocompleteEnabled(true);
          return true;
        }
        return false;
      } catch (serproErr) {
        const code = String(serproErr?.code || serproErr?.data?.error || "").toLowerCase();
        if (code === "feature_disabled") {
          setSerproAutocompleteEnabled(false);
          return false;
        }
        const detail = String(
          serproErr?.detail || serproErr?.message || serproErr?.data?.message || "",
        ).trim();
        if (detail) {
          setLookupNotice(
            `${intl.get("baas.entidades.create.lookup.notFound")} SERPRO: ${detail}`,
          );
          setLookupNoticeTone("warn");
        }
        return false;
      } finally {
        serproInFlightRef.current = false;
      }
    },
    [applySerproDraft],
  );

  useEffect(() => {
    if (!active) {
      return;
    }

    const initialInstanceId =
      fixedInstanceId || selectedInstance?.id || instances[0]?.id || "";

    if (!readOnly && !editMode) {
      // Create mode must start empty — discard any prior session draft for this instance.
      clearEntidadeDraft(initialInstanceId);
    }

    setStepIndex(0);
    setActiveOwnerIndex(0);
    setForm(
      normalizeDraftForm(
        (readOnly || editMode) && initialForm ? initialForm : createInitialBusinessCreateForm(),
      ),
    );
    setInstanceId(initialInstanceId);
    setDebugExpanded(readOnly || editMode ? false : debugConsoleExpanded);
    setJsonText(getDefaultBusinessAccountPayloadJson());
    setDebugError("");
    setError("");
    setValidationIssues([]);
    setSaveSuccess("");
    setSubmitting(false);
    setLookingUp(false);
    setLookupNotice("");
    setLookupNoticeTone("info");
    setExistingEntityId(null);
    setOwnerFieldError("");
    serproLastDocRef.current = "";
    serproInFlightRef.current = false;
    // Reset only when entering create/view / switching selected instance — not on instances[] identity churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [active, selectedInstance?.id, debugConsoleExpanded, readOnly, editMode, initialForm, fixedInstanceId]);

  useEffect(() => {
    if (!active || !instanceId || readOnly || editMode) {
      return;
    }
    saveEntidadeDraft(instanceId, { form, stepIndex });
  }, [active, instanceId, form, stepIndex, readOnly, editMode]);

  const currentStep = BAAS_ENTIDADE_CREATE_STEPS[stepIndex];
  const isLastStep = stepIndex === BAAS_ENTIDADE_CREATE_STEPS.length - 1;
  const ownerIndex = Math.min(activeOwnerIndex, Math.max(0, form.beneficialOwners.length - 1));
  const owner = form.beneficialOwners[ownerIndex] ?? {};
  const ownerLinked = ownerHasLinkedPerson(owner);
  const persistedOwnerPersIds = useMemo(() => {
    if (!editMode) return new Set();
    return new Set(
      (initialForm?.beneficialOwners || [])
        .map((item) => String(item?.persId || "").trim().toLowerCase())
        .filter((id) => id.startsWith("pers_")),
    );
  }, [editMode, initialForm]);
  const isPersistedOwnerRow = (item) =>
    isExistingBusinessOwnerRow(item, persistedOwnerPersIds);
  const isPersistedOwner = isPersistedOwnerRow(owner);
  const canManageOwners = !isViewOnly;
  const linkedOwnerFieldsLocked = isViewOnly || ownerLinked;
  const ownerAccountLocked = isViewOnly || isPersistedOwner;
  const participationFlagsLocked = isViewOnly;
  const lastBeneficialOwner = isLastRequiredBeneficialOwner(form.beneficialOwners, ownerIndex);
  const visible = useMemo(() => getVisibleFields(form, owner), [form, owner]);
  const documentContinueEnabled = useMemo(() => {
    if (!isCreateFlow) {
      return true;
    }
    if (!form.countryOfIncorporation?.trim()) {
      return false;
    }
    if (isUsCountry(form.countryOfIncorporation)) {
      return Boolean(form.einPending) || Boolean(getBusinessLookupDocumentDigits(form));
    }
    const digits = getBusinessLookupDocumentDigits(form);
    if (isBrCountry(form.countryOfIncorporation)) {
      return isValidBusinessCnpj(digits);
    }
    return Boolean(digits);
  }, [isCreateFlow, form]);

  // Feature on → consult SERPRO as soon as a valid BR CNPJ is typed (not only on Continuar).
  useEffect(() => {
    if (!active || !isCreateFlow || !serproAutocompleteEnabled) return undefined;
    if (currentStep !== "document") return undefined;
    if (!isBrCountry(form.countryOfIncorporation)) return undefined;

    const digits = getBusinessLookupDocumentDigits(form);
    if (!digits || digits.length !== 14 || !isValidBusinessCnpj(digits)) return undefined;
    if (serproLastDocRef.current === digits) return undefined;

    const instance =
      instances.find((item) => item.id === instanceId) || selectedInstance || activeInstance;
    if (!instance) return undefined;

    const timer = window.setTimeout(() => {
      setLookingUp(true);
      Promise.resolve()
        .then(async () => {
          const result = await lookupBusinessEntityByDocument(instance, digits, {
            entityType: "BUSINESS",
          });
          if (result.found && result.entity?.id) {
            setExistingEntityId(result.entity.id);
            setLookupNotice(intl.get("baas.entidades.create.lookup.alreadyEntity"));
            setLookupNoticeTone("warn");
            serproLastDocRef.current = digits;
            return;
          }
          setExistingEntityId(null);
          await trySerproAutocomplete(instance, digits);
        })
        .catch(() => {
          // Continuar retries.
        })
        .finally(() => {
          setLookingUp(false);
        });
    }, 450);

    return () => window.clearTimeout(timer);
  }, [
    active,
    isCreateFlow,
    serproAutocompleteEnabled,
    currentStep,
    form.countryOfIncorporation,
    form.registrationId?.number,
    instanceId,
    instances,
    selectedInstance,
    activeInstance,
    trySerproAutocomplete,
  ]);

  const usedOwnerPersIds = useMemo(
    () =>
      (form.beneficialOwners || [])
        .map((item) => String(item?.persId || "").trim())
        .filter((id) => id.toLowerCase().startsWith("pers_")),
    [form.beneficialOwners],
  );
  const industryRequirements = useMemo(
    () => getIndustryRequirements(form.industry),
    [form.industry],
  );
  const availableLegalTypes = useMemo(
    () => getLegalTypesForCountry(form.countryOfIncorporation),
    [form.countryOfIncorporation],
  );
  const availablePaymentRails = useMemo(
    () => getPaymentRailsForCountry(form.countryOfIncorporation),
    [form.countryOfIncorporation],
  );
  const processingRisk = useMemo(
    () => evaluateEntityProcessingRisk(form, industryRequirements),
    [form, industryRequirements],
  );
  const fieldRequired = (fieldPath) => isFieldRequiredForIndustry(fieldPath, industryRequirements);
  const ownerFieldRequired = (fieldSuffix) =>
    isOwnerFieldRequired(fieldSuffix, industryRequirements);
  const isCompact = variant === "compact";
  const reviewClassName = isCompact
    ? "max-h-[420px] space-y-4 overflow-y-auto rounded-xl border border-gray-100 bg-gray-50 p-4 text-sm dark:border-brand-dark-border dark:bg-brand-dark-surface"
    : "space-y-4 rounded-xl border border-gray-100 bg-gray-50 p-4 text-sm dark:border-brand-dark-border dark:bg-brand-dark-surface";

  const updateOwner = (patch) => {
    setForm((current) => {
      const exclusiveControl = patch.isControlPerson === true;
      return {
        ...current,
        beneficialOwners: current.beneficialOwners.map((item, index) => {
          if (index === ownerIndex) {
            return { ...item, ...patch };
          }
          if (exclusiveControl) {
            return { ...item, isControlPerson: false };
          }
          return item;
        }),
      };
    });
  };

  const updateOwnerAddress = (patch) => {
    setForm((current) => ({
      ...current,
      beneficialOwners: current.beneficialOwners.map((item, index) =>
        index === ownerIndex ? { ...item, address: { ...item.address, ...patch } } : item,
      ),
    }));
  };

  const updateOwnerDoc = (docKey, patch) => {
    setOwnerFieldError("");
    setForm((current) => ({
      ...current,
      beneficialOwners: current.beneficialOwners.map((item, index) =>
        index === ownerIndex ? { ...item, [docKey]: { ...item[docKey], ...patch } } : item,
      ),
    }));
  };

  const handleAddOwner = () => {
    if (form.beneficialOwners.length >= MAX_BENEFICIAL_OWNERS) {
      return;
    }
    setForm((current) => ({
      ...current,
      beneficialOwners: [...current.beneficialOwners, createEmptyBeneficialOwner()],
    }));
    setActiveOwnerIndex(form.beneficialOwners.length);
  };

  const handleRemoveOwner = (index) => {
    if (form.beneficialOwners.length <= 1) {
      return;
    }
    setForm((current) => ({
      ...current,
      beneficialOwners: current.beneficialOwners.filter((_, itemIndex) => itemIndex !== index),
    }));
    setActiveOwnerIndex((current) => {
      if (current === index) {
        return Math.max(0, index - 1);
      }
      if (current > index) {
        return current - 1;
      }
      return current;
    });
  };

  const handleSelectExistingPerson = (personEntity) => {
    if (!canManageOwners || !personEntity) return;
    const othersHaveControl = form.beneficialOwners.some(
      (item, itemIndex) => itemIndex !== ownerIndex && item.isControlPerson,
    );
    const linked = mapPersonEntityToBeneficialOwner(personEntity, {
      isBeneficialOwner: owner.isBeneficialOwner ?? true,
      isControlPerson: Boolean(owner.isControlPerson) || !othersHaveControl,
      ownershipPercentage: owner.ownershipPercentage ?? "",
      role: owner.role || "owner",
    });
    if (!linked.persId) {
      setError(intl.get("baas.entidades.create.existingPersonInvalid"));
      return;
    }
    if (
      usedOwnerPersIds.some(
        (id) => id.toLowerCase() === linked.persId.toLowerCase() && id !== owner.persId,
      )
    ) {
      setError(intl.get("baas.entidades.create.existingPersonDuplicate"));
      return;
    }

    const currentIsBlank =
      !ownerHasLinkedPerson(owner) &&
      !owner.firstName?.trim() &&
      !owner.lastName?.trim() &&
      !owner.email?.trim();

    setError("");
    if (currentIsBlank) {
      updateOwner(linked);
      return;
    }

    if (form.beneficialOwners.length >= MAX_BENEFICIAL_OWNERS) {
      setError(intl.get("baas.entidades.create.existingPersonMaxOwners"));
      return;
    }

    setForm((current) => ({
      ...current,
      beneficialOwners: [...current.beneficialOwners, linked],
    }));
    setActiveOwnerIndex(form.beneficialOwners.length);
  };

  const handleUnlinkExistingPerson = () => {
    if (!canManageOwners || !ownerLinked || isPersistedOwner) return;
    updateOwner({
      ...createEmptyBeneficialOwner(),
      isBeneficialOwner: owner.isBeneficialOwner,
      isControlPerson: owner.isControlPerson,
      ownershipPercentage: owner.ownershipPercentage,
      role: owner.role,
    });
  };

  const renderLinkedPersonBanner = () => {
    if (!ownerLinked) return null;
    const persId = String(owner.persId || "").trim();
    return (
      <div className="pointer-events-auto rounded-lg border border-emerald-100 bg-emerald-50/70 px-3 py-2 text-sm dark:border-emerald-900/40 dark:bg-emerald-950/20">
        <p className="font-medium text-emerald-800 dark:text-emerald-300">
          {intl.get("baas.entidades.create.existingPersonLinkedTitle")}
        </p>
        <p className="mt-0.5 font-mono text-xs text-emerald-700 dark:text-emerald-400">{persId}</p>
        <p className="mt-1 text-xs text-emerald-700/90 dark:text-emerald-400/90">
          {intl.get("baas.entidades.create.existingPersonLinkedHint")}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <PersonConsoleLink persId={persId}>
            {intl.get("baas.entidades.create.openPersonDocuments")}
          </PersonConsoleLink>
          {canManageOwners && !isPersistedOwner ? (
            <button
              type="button"
              className="text-xs font-medium text-gray-500 underline dark:text-gray-400"
              onClick={handleUnlinkExistingPerson}
            >
              {intl.get("baas.entidades.create.existingPersonUnlink")}
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  const renderOwnerSelector = () => (
    <div className="pointer-events-auto space-y-3">
      <OwnerSelectorBar
        owners={form.beneficialOwners}
        activeIndex={ownerIndex}
        onSelect={setActiveOwnerIndex}
        onAdd={handleAddOwner}
        onAddExisting={() => setPersonPickerOpen(true)}
        onRemove={handleRemoveOwner}
        readOnly={!canManageOwners}
        canRemoveIndex={(index) => {
          if (!canManageOwners || form.beneficialOwners.length <= 1) return false;
          if (isEditableDetail && isPersistedOwnerRow(form.beneficialOwners[index])) return false;
          return true;
        }}
      />
      {isEditableDetail ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {intl.get("baas.entidades.create.editOwnersHint")}
        </p>
      ) : null}
      {renderLinkedPersonBanner()}
      {currentStep === "ownerAccount" || currentStep === "review" ? (
        <OwnershipTotalBanner owners={form.beneficialOwners} />
      ) : null}
    </div>
  );

  const handleSaveContact = async () => {
    const profileSteps = ["countryAndType", "incorporation", "businessAddress", "activityAndUsage"];
    for (const step of profileSteps) {
      const result = validateBusinessFormStep(step, form, { visible, industryRequirements });
      if (!result.valid) {
        setError(intl.get(result.errorKey ?? "baas.entidades.create.incompleteRequiredFields"));
        return false;
      }
    }
    if (!entityId) {
      setError(intl.get("baas.entidades.detail.saveError"));
      return false;
    }
    const instance =
      instances.find((item) => item.id === instanceId) || selectedInstance || activeInstance;
    if (!instance) {
      setError(intl.get("baas.entidades.create.instanceRequired"));
      return false;
    }

    const ownerIdentityStepIndex = BAAS_ENTIDADE_CREATE_STEPS.indexOf("ownerIdentity");
    const focusOwnerIssue = (formOwnerIndex, message) => {
      if (formOwnerIndex >= 0) setActiveOwnerIndex(formOwnerIndex);
      if (ownerIdentityStepIndex >= 0) setStepIndex(ownerIdentityStepIndex);
      setOwnerFieldError(message);
      setError(message);
    };

    setSubmitting(true);
    setError("");
    setOwnerFieldError("");
    setSaveSuccess("");
    const newOwners = selectNewBeneficialOwnersForSave(
      form.beneficialOwners,
      persistedOwnerPersIds,
    );
    try {
      if (newOwners.length > 0) {
        const ownerSteps = ["ownerPersonal", "ownerAddress", "ownerIdentity", "ownerAccount"];
        for (const step of ownerSteps) {
          const result = validateBusinessFormStep(step, { ...form, beneficialOwners: newOwners }, {
            visible,
            industryRequirements,
          });
          if (!result.valid) {
            setError(intl.get(result.errorKey ?? "baas.entidades.create.incompleteRequiredFields"));
            setSubmitting(false);
            return false;
          }
        }

        for (const owner of newOwners) {
          if (ownerHasLinkedPerson(owner) || hasAnyCompleteOwnerIdentityDoc(owner)) continue;
          const formIndex = form.beneficialOwners.indexOf(owner);
          const labelIndex = formIndex >= 0 ? formIndex + 1 : 1;
          const message = hasAnyOwnerIdentityDocNumber(owner)
            ? intl.get("baas.entidades.create.identityDocIncomplete")
            : intl.get("baas.entidades.validation.ownerDocumentRequired", { index: labelIndex });
          focusOwnerIssue(formIndex, message);
          setSubmitting(false);
          return false;
        }
      }

      if (newOwners.length > 0) {
        await addBaasEntityBeneficialOwners(
          instance,
          entityId,
          newOwners.map((item) =>
            mapBeneficialOwnerToApiPayload(item, { isHighRisk: Boolean(form.isHighRisk) }),
          ),
        );
      }

      const patch = buildBusinessEntityContactPatch(form);
      if (unlockIdentity && form.businessName) {
        const nextName = String(form.businessName).trim();
        const prevName = String(initialForm?.businessName || "").trim();
        // Only send legal_name when Valut admin actually changed it — echo trips Baas gate.
        if (nextName && nextName.toLowerCase() !== prevName.toLowerCase()) {
          patch.legal_name = nextName;
        }
      }
      const updated = await updateBaasEntityContact(
        instance,
        entityId,
        patch,
      );
      let refreshed = updated;
      try {
        refreshed = (await fetchBaasEntityById(instance, entityId)) || updated;
      } catch {
        refreshed = updated;
      }
      setSaveSuccess(intl.get("baas.entidades.detail.saveSuccess"));
      onUpdated?.(refreshed);
      return true;
    } catch (err) {
      const message = formatBaasEntitySaveError(
        err,
        intl.get("baas.entidades.detail.saveError"),
      );
      const indexes = getBeneficialOwnerIndexesFromError(err);
      if (indexes.length > 0) {
        const posted = newOwners[indexes[0]];
        const formIndex = posted
          ? form.beneficialOwners.indexOf(posted)
          : indexes[0];
        focusOwnerIssue(formIndex >= 0 ? formIndex : 0, message);
      } else {
        setError(message);
      }
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const clearLookupState = () => {
    setLookupNotice("");
    setLookupNoticeTone("info");
    setExistingEntityId(null);
    serproLastDocRef.current = "";
  };

  const handleCountryChange = (countryCode) => {
    const nextLegalType = getDefaultLegalTypeForCountry(countryCode);
    const railsForCountry = getPaymentRailsForCountry(countryCode);
    clearLookupState();
    setForm((current) => ({
      ...current,
      countryOfIncorporation: countryCode,
      legalType: nextLegalType,
      stateOfIncorporation: "",
      expectedPaymentRails: (current.expectedPaymentRails ?? []).filter((rail) =>
        railsForCountry.includes(rail),
      ),
      ein: isUsCountry(countryCode) ? current.ein : "",
      einPending: isUsCountry(countryCode) ? current.einPending : false,
      registrationId: {
        number: isUsCountry(countryCode) ? "" : current.registrationId.number,
        // Document country drives registration country — never leave a stale US default.
        countryCode: isUsCountry(countryCode) ? "" : countryCode,
      },
      address: {
        ...current.address,
        countryCode: countryCode || current.address.countryCode,
      },
    }));
  };

  const handleIndustryChange = (industry) => {
    const requirements = getIndustryRequirements(industry);
    setForm((current) => {
      const nextForm = {
        ...current,
        industry,
        isHighRisk: requirements.autoSetHighRisk ? true : current.isHighRisk,
      };
      const risk = evaluateEntityProcessingRisk(nextForm, requirements);
      return { ...nextForm, isHighRisk: risk.isHighRisk };
    });
  };

  const syncProcessingRisk = (patch) => {
    setForm((current) => {
      const nextForm = { ...current, ...patch };
      const risk = evaluateEntityProcessingRisk(nextForm, industryRequirements);
      return { ...nextForm, isHighRisk: risk.isHighRisk };
    });
  };

  const validateStep = () => {
    const result = validateBusinessFormStep(currentStep, form, {
      visible,
      industryRequirements,
    });

    if (!result.valid) {
      const message = intl.get(result.errorKey ?? "baas.entidades.create.incompleteRequiredFields");
      setError(message);
      setValidationIssues([{ step: currentStep, errorKey: result.errorKey }]);
      return false;
    }

    if ((currentStep === "document" || currentStep === "companyIdentity") && !instanceId) {
      setError(intl.get("baas.entidades.create.instanceRequired"));
      setValidationIssues([
        { step: currentStep, errorKey: "baas.entidades.create.instanceRequired" },
      ]);
      return false;
    }

    setError("");
    setValidationIssues([]);
    return true;
  };

  const runDocumentLookup = async () => {
    if (form.einPending && isUsCountry(form.countryOfIncorporation)) {
      setLookupNotice(intl.get("baas.entidades.create.lookup.einPendingSkip"));
      setLookupNoticeTone("info");
      setExistingEntityId(null);
      return true;
    }

    const digits = getBusinessLookupDocumentDigits(form);
    if (!digits) {
      setError(intl.get("baas.entidades.create.lookup.documentRequired"));
      setLookupNotice("");
      setExistingEntityId(null);
      return false;
    }

    if (isBrCountry(form.countryOfIncorporation) && !isValidBusinessCnpj(digits)) {
      setError(intl.get("baas.entidades.create.cnpjInvalid"));
      setValidationIssues([{ step: "document", errorKey: "baas.entidades.create.cnpjInvalid" }]);
      return false;
    }

    const instance =
      instances.find((item) => item.id === instanceId) || selectedInstance || activeInstance;
    if (!instance) {
      setError(intl.get("baas.entidades.create.instanceRequired"));
      return false;
    }

    setLookingUp(true);
    try {
      const result = await lookupBusinessEntityByDocument(instance, digits, {
        entityType: "BUSINESS",
      });

      if (!result.found || !result.entity?.id) {
        setLookupNotice(intl.get("baas.entidades.create.lookup.notFound"));
        setLookupNoticeTone("info");
        setExistingEntityId(null);

        if (isBrCountry(form.countryOfIncorporation) && digits.length === 14) {
          // Always attempt: API enforces feature; avoids false negatives when features.read fails.
          await trySerproAutocomplete(instance, digits, { force: true });
        }

        return true;
      }

      setExistingEntityId(result.entity.id);
      setLookupNotice(intl.get("baas.entidades.create.lookup.alreadyEntity"));
      setLookupNoticeTone("warn");
      return false;
    } catch (err) {
      const detail = String(err?.message || err?.data?.message || "").trim();
      setError(
        detail
          ? `${intl.get("baas.entidades.create.lookup.failed")} ${detail}`
          : intl.get("baas.entidades.create.lookup.failed"),
      );
      return false;
    } finally {
      setLookingUp(false);
    }
  };

  const handleNext = async () => {
    if (isEditableDetail) {
      if (currentStep === "ownerAccount") {
        const saved = await handleSaveContact();
        if (!saved) return;
      }
      setError("");
      setValidationIssues([]);
      setStepIndex((current) => getNextWizardStepIndex(current, industryRequirements));
      return;
    }

    if (!isViewOnly && isCreateFlow && currentStep === "document" && !documentContinueEnabled) {
      if (!form.countryOfIncorporation?.trim()) {
        setError(intl.get("baas.entidades.create.countryRequired"));
      } else if (isBrCountry(form.countryOfIncorporation)) {
        setError(intl.get("baas.entidades.create.cnpjInvalid"));
      } else if (isUsCountry(form.countryOfIncorporation)) {
        setError(intl.get("baas.entidades.create.einRequired"));
      } else {
        setError(intl.get("baas.entidades.create.registrationRequired"));
      }
      return;
    }

    if (!isViewOnly && !validateStep()) {
      return;
    }

    if (!isViewOnly && isCreateFlow && currentStep === "document") {
      const canContinue = await runDocumentLookup();
      if (!canContinue) {
        return;
      }
    }

    setError("");
    setValidationIssues([]);
    setStepIndex((current) => getNextWizardStepIndex(current, industryRequirements));
  };

  const handleBack = () => {
    setError("");
    setValidationIssues([]);
    setStepIndex((current) => getPreviousWizardStepIndex(current, industryRequirements));
  };

  const handleStepClick = (index) => {
    setError("");
    setValidationIssues([]);
    setStepIndex(index);
  };

  const handleInstanceChange = (nextInstanceId) => {
    if (instanceId && nextInstanceId !== instanceId) {
      clearEntidadeDraft(instanceId);
    }

    clearEntidadeDraft(nextInstanceId);
    setInstanceId(nextInstanceId);
    setForm(normalizeDraftForm(createInitialBusinessCreateForm()));
    setStepIndex(0);
    setActiveOwnerIndex(0);
    clearLookupState();
    setError("");
    setValidationIssues([]);
  };

  const handleCancel = () => {
    if (submitting) {
      return;
    }
    onCancel?.();
  };

  const handleSyncJsonFromForm = () => {
    try {
      const payload = buildBusinessAccountPayload(form, { industryRequirements });
      setJsonText(JSON.stringify(payload, null, 2));
      setDebugError("");
    } catch {
      setDebugError(intl.get("baas.entidades.create.jsonFormatError"));
    }
  };

  const handleFormatJson = () => {
    try {
      setJsonText(JSON.stringify(JSON.parse(jsonText), null, 2));
      setDebugError("");
    } catch {
      setDebugError(intl.get("baas.entidades.create.jsonFormatError"));
    }
  };

  const handleRestoreTemplate = () => {
    setJsonText(getDefaultBusinessAccountPayloadJson());
    setDebugError("");
  };

  const submitPayload = async (payload) => {
    if (!instanceId) {
      setError(intl.get("baas.entidades.create.instanceRequired"));
      return;
    }

    if (existingEntityId) {
      setError(intl.get("baas.entidades.create.lookup.alreadyEntity"));
      setLookupNoticeTone("warn");
      return;
    }

    setSubmitting(true);
    setError("");
    setValidationIssues([]);
    setDebugError("");

    try {
      const instance =
        instances.find((item) => item.id === instanceId) || selectedInstance;
      const result = await createBusinessAccount(instance, payload);
      clearEntidadeDraft(instanceId);
      onCreated?.(result);
    } catch (err) {
      const conflictMessage = resolveEntityCreateConflictMessage(
        err,
        "baas.entidades.create.error",
      );
      setError(conflictMessage);

      const code = String(err?.code || err?.data?.error || "").toLowerCase();
      if (err?.status === 409 || code === "entity_already_exists") {
        const digits = getBusinessLookupDocumentDigits(form);
        const instance =
          instances.find((item) => item.id === instanceId) || selectedInstance || activeInstance;
        if (digits && instance) {
          try {
            const lookup = await lookupBusinessEntityByDocument(instance, digits, {
              entityType: "BUSINESS",
            });
            if (lookup.entity?.id) {
              setExistingEntityId(lookup.entity.id);
              setLookupNotice(intl.get("baas.entidades.create.lookup.alreadyEntity"));
              setLookupNoticeTone("warn");
            }
          } catch {
            // Keep conflict message only.
          }
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDebugSubmit = async () => {
    if (!instanceId) {
      setDebugError(intl.get("baas.entidades.create.instanceRequired"));
      return;
    }

    try {
      const payload = parseBusinessAccountPayloadJson(jsonText);
      await submitPayload(payload);
    } catch {
      setDebugError(intl.get("baas.entidades.create.jsonInvalid"));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isEditableDetail) {
      await handleSaveContact();
      return;
    }

    // Revisão is preview-only. Never create from tab click / Enter / button reuse.
    if (!isLastStep) {
      return;
    }

    if (existingEntityId) {
      setError(intl.get("baas.entidades.create.lookup.alreadyEntity"));
      setLookupNotice(intl.get("baas.entidades.create.lookup.alreadyEntity"));
      setLookupNoticeTone("warn");
      return;
    }

    const submitValidation = validateBusinessFormForSubmit(form, {
      visible,
      industryRequirements,
    });

    if (!submitValidation.valid) {
      setValidationIssues(submitValidation.issues || []);
      setError(intl.get("baas.entidades.create.formIncompleteAlert"));
      return;
    }

    if (!instanceId) {
      setError(intl.get("baas.entidades.create.instanceRequired"));
      setValidationIssues([
        { step: "document", errorKey: "baas.entidades.create.instanceRequired" },
      ]);
      return;
    }

    setValidationIssues([]);
    await submitPayload(buildBusinessAccountPayload(form, { industryRequirements }));
  };

  const optionLabel = (group, value) =>
    intl.get(`baas.entidades.create.options.${group}.${value}`);
  const legalTypeLabel = (value) => optionLabel("legalType", value);
  const enumLabel = (prefix, value) => optionLabel(prefix, value);

  const renderInstanceSelector = () => (
    <Field label={intl.get("baas.entidades.columns.instance")} required={!identityLocked}>
      <select
        className={inputClass}
        value={instanceId}
        onChange={(event) => handleInstanceChange(event.target.value)}
        required={!identityLocked}
        disabled={identityLocked}
      >
        <option value="" disabled>
          {intl.get("baas.entidades.create.selectInstance")}
        </option>
        {instances.map((instance) => (
          <option key={instance.id} value={instance.id}>
            {instance.name}
          </option>
        ))}
      </select>
    </Field>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case "document": {
        const isBr = isBrCountry(form.countryOfIncorporation);
        const isUs = isUsCountry(form.countryOfIncorporation);
        const hasCountry = Boolean(form.countryOfIncorporation);
        const documentDigits = getBusinessLookupDocumentDigits(form);
        const showCnpjInvalid =
          isBr && documentDigits.length === 14 && !isValidBusinessCnpj(documentDigits);

        return (
          <div className="space-y-5">
            <StepHeader
              title={intl.get("baas.entidades.create.step.document")}
              description={intl.get("baas.entidades.create.stepDesc.document")}
            />

            {!selectedInstance && !identityLocked ? (
              <div className="max-w-md">{renderInstanceSelector()}</div>
            ) : null}

            <div className="space-y-4 rounded-xl border border-gray-100 bg-gray-50/60 p-4 sm:p-5 dark:border-brand-dark-border dark:bg-brand-dark-surface">
              <CountrySelectField
                label={intl.get("baas.entidades.create.documentCountry")}
                value={form.countryOfIncorporation}
                onChange={handleCountryChange}
                countryOptions={countryOptions}
                required
                disabled={identityLocked || lookingUp || submitting}
              />

              {hasCountry && isUs ? (
                <>
                  <Field label={intl.get("baas.entidades.create.ein")} required={!form.einPending}>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      autoFocus
                      className={`${inputClass} h-12 text-base tracking-wide sm:text-lg`}
                      value={form.ein}
                      onChange={(event) => {
                        clearLookupState();
                        setError("");
                        setForm((current) => ({ ...current, ein: event.target.value }));
                      }}
                      disabled={form.einPending || identityLocked || lookingUp || submitting}
                      readOnly={identityLocked}
                    />
                  </Field>
                  <CheckboxField
                    label={intl.get("baas.entidades.create.einPending")}
                    checked={form.einPending}
                    onChange={(event) => {
                      clearLookupState();
                      setError("");
                      setForm((current) => ({ ...current, einPending: event.target.checked }));
                    }}
                    disabled={identityLocked || lookingUp || submitting}
                  />
                </>
              ) : null}

              {hasCountry && !isUs ? (
                <>
                  <Field
                    label={
                      isBr
                        ? intl.get("baas.entidades.create.cnpjLabel")
                        : intl.get("baas.entidades.create.registrationNumber")
                    }
                    required
                  >
                    <input
                      type="text"
                      inputMode={isBr ? "numeric" : "text"}
                      autoComplete="off"
                      autoFocus
                      className={`${inputClass} h-12 text-base tracking-wide sm:text-lg`}
                      value={form.registrationId.number}
                      placeholder={
                        isBr ? intl.get("baas.entidades.create.cnpjPlaceholder") : undefined
                      }
                      onChange={(event) => {
                        clearLookupState();
                        setError("");
                        const raw = event.target.value;
                        setForm((current) => ({
                          ...current,
                          registrationId: {
                            number: isBr ? formatBusinessDocumentInput(raw) : raw,
                            countryCode: current.countryOfIncorporation,
                          },
                        }));
                      }}
                      required
                      disabled={identityLocked || lookingUp || submitting}
                      readOnly={identityLocked}
                      aria-invalid={showCnpjInvalid ? "true" : undefined}
                    />
                  </Field>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {intl.get(
                      isBr && serproAutocompleteEnabled
                        ? "baas.entidades.create.cnpjFieldHintSerpro"
                        : isBr
                          ? "baas.entidades.create.cnpjFieldHint"
                          : "baas.entidades.create.documentStepHint",
                    )}
                  </p>
                  {isBr && form.businessName?.trim() && lookupNoticeTone === "success" ? (
                    <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-100">
                      <span className="font-semibold">
                        {intl.get("baas.entidades.create.lookup.serproCompanyLabel") || "Razão social"}
                        {": "}
                      </span>
                      {form.businessName.trim()}
                    </p>
                  ) : null}
                  {showCnpjInvalid ? (
                    <p className="text-xs font-medium text-red-600 dark:text-red-400">
                      {intl.get("baas.entidades.create.cnpjInvalid")}
                    </p>
                  ) : null}
                </>
              ) : null}

              {!hasCountry ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {intl.get("baas.entidades.create.documentStepHint")}
                </p>
              ) : null}
            </div>

            {lookingUp ? <LookupSkeleton /> : null}
            {!lookingUp ? (
              <LookupResultBanner
                notice={lookupNotice}
                tone={lookupNoticeTone}
                existingEntityId={existingEntityId}
                onOpenEntity={() => {
                  if (existingEntityId) {
                    history.push(buildBusinessEntityDetailPath(existingEntityId));
                  }
                }}
              />
            ) : null}
          </div>
        );
      }

      case "countryAndType":
        return (
          <>
            <StepHeader
              title={intl.get("baas.entidades.create.step.countryAndType")}
              description={intl.get("baas.entidades.create.stepDesc.countryAndType")}
            />
            <SectionHeading>{intl.get("baas.entidades.create.section.companyType")}</SectionHeading>
            <CountrySelectField
              label={intl.get("baas.entidades.create.countryOfIncorporation")}
              value={form.countryOfIncorporation}
              onChange={handleCountryChange}
              countryOptions={countryOptions}
              required
              disabled={identityLocked || isCreateFlow}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={intl.get("baas.entidades.create.legalType")} required>
                <select
                  className={inputClass}
                  value={form.legalType}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, legalType: event.target.value }))
                  }
                  disabled={!form.countryOfIncorporation}
                >
                  {availableLegalTypes.map((type) => (
                    <option key={type} value={type}>
                      {legalTypeLabel(type)}
                    </option>
                  ))}
                </select>
              </Field>
              <StateOfIncorporationField
                countryCode={form.countryOfIncorporation}
                value={form.stateOfIncorporation}
                onChange={(value) =>
                  setForm((current) => ({ ...current, stateOfIncorporation: value }))
                }
              />
            </div>
            {(visible.registrationId || visible.ein) && form.countryOfIncorporation ? (
              <p className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 dark:border-brand-dark-border dark:bg-brand-dark-surface dark:text-gray-200">
                {visible.ein
                  ? `${intl.get("baas.entidades.create.ein")}: ${
                      form.einPending
                        ? intl.get("baas.entidades.create.einPending")
                        : form.ein || "—"
                    }`
                  : `${
                      isBrCountry(form.countryOfIncorporation)
                        ? intl.get("baas.entidades.create.cnpjLabel")
                        : intl.get("baas.entidades.create.registrationNumber")
                    }: ${form.registrationId.number || "—"} · ${form.registrationId.countryCode || "—"}`}
              </p>
            ) : null}
          </>
        );

      case "companyIdentity": {
        const contactRequired = isBrCountry(form.countryOfIncorporation);
        return (
          <>
            <StepHeader
              title={intl.get("baas.entidades.create.step.companyIdentity")}
              description={intl.get("baas.entidades.create.stepDesc.companyIdentity")}
            />
            {!isCreateFlow ? (
              <>
                <SectionHeading>{intl.get("baas.entidades.create.section.context")}</SectionHeading>
                {renderInstanceSelector()}
              </>
            ) : null}
            <SectionHeading>{intl.get("baas.entidades.create.section.names")}</SectionHeading>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={intl.get("baas.entidades.create.businessName")} required>
                <input
                  type="text"
                  className={inputClass}
                  value={form.businessName}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, businessName: event.target.value }))
                  }
                  required
                  disabled={identityLocked}
                  readOnly={identityLocked}
                />
              </Field>
              <Field label={intl.get("baas.entidades.create.dbaName")} optional>
                <input
                  type="text"
                  className={inputClass}
                  value={form.dbaName}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, dbaName: event.target.value }))
                  }
                  disabled={identityLocked}
                  readOnly={identityLocked}
                />
              </Field>
            </div>
            <SectionHeading>{intl.get("baas.entidades.create.section.contact")}</SectionHeading>
            <ConditionalHelper messageKey="baas.entidades.create.contactBrHint" />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label={intl.get("baas.entidades.create.email")}
                required={contactRequired}
                optional={!contactRequired}
              >
                <input
                  type="email"
                  className={inputClass}
                  value={form.email ?? ""}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, email: event.target.value }))
                  }
                  required={contactRequired}
                  disabled={isViewOnly}
                  readOnly={isViewOnly}
                />
              </Field>
            </div>
            <PhonePartsFields
              value={form.phone}
              onChange={(patch) =>
                setForm((current) => ({
                  ...current,
                  phone: { ...(current.phone || {}), ...patch },
                }))
              }
              dialOptions={phoneDialOptions}
              required={contactRequired}
              optional={!contactRequired}
              disabled={isViewOnly}
              readOnly={isViewOnly}
              className="mt-4"
            />
          </>
        );
      }

      case "incorporation":
        return (
          <>
            <StepHeader
              title={intl.get("baas.entidades.create.step.incorporation")}
              description={intl.get("baas.entidades.create.stepDesc.incorporation")}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={intl.get("baas.entidades.create.dateOfIncorporation")} required>
                <input
                  type="date"
                  className={inputClass}
                  value={form.dateOfIncorporation}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      dateOfIncorporation: event.target.value,
                    }))
                  }
                  required
                />
              </Field>
              <Field label={intl.get("baas.entidades.create.yearOfIncorporation")} optional>
                <input
                  type="text"
                  className={inputClass}
                  value={form.yearOfIncorporation}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      yearOfIncorporation: event.target.value,
                    }))
                  }
                  maxLength={4}
                />
              </Field>
            </div>
            <CheckboxField
              label={intl.get("baas.entidades.create.hasVirtualAddress")}
              checked={form.hasVirtualAddress}
              onChange={(event) =>
                setForm((current) => ({ ...current, hasVirtualAddress: event.target.checked }))
              }
            />
          </>
        );

      case "businessAddress":
        return (
          <>
            <StepHeader
              title={intl.get("baas.entidades.create.step.businessAddress")}
              description={
                isEditableDetail
                  ? intl.get("baas.entidades.detail.editBusinessAddressHint")
                  : intl.get("baas.entidades.create.stepDesc.businessAddress")
              }
            />
            {isViewOnly ? (
              <div className="pointer-events-none opacity-95">
                {isBrCountry(form.address?.countryCode) ? (
                  <BrAddressFieldsGrid
                    address={form.address}
                    onChange={() => {}}
                    required
                    labelPrefix="baas.entidades.create.address"
                    countryOptions={countryOptions}
                  />
                ) : (
                  <AddressFieldsGrid
                    address={form.address}
                    onChange={() => {}}
                    required
                    countryOptions={countryOptions}
                  />
                )}
              </div>
            ) : (
              <div className="baas-address-editable">
                {isBrCountry(form.address?.countryCode) ? (
                  <BrAddressFieldsGrid
                    address={form.address}
                    onChange={(patch) =>
                      setForm((current) => ({
                        ...current,
                        address: { ...current.address, ...patch },
                      }))
                    }
                    required
                    labelPrefix="baas.entidades.create.address"
                    countryOptions={countryOptions}
                  />
                ) : (
                  <AddressFieldsGrid
                    address={form.address}
                    onChange={(patch) =>
                      setForm((current) => ({
                        ...current,
                        address: { ...current.address, ...patch },
                      }))
                    }
                    required
                    countryOptions={countryOptions}
                  />
                )}
              </div>
            )}
          </>
        );

      case "activityAndUsage":
        return (
          <>
            <StepHeader
              title={intl.get("baas.entidades.create.step.activityAndUsage")}
              description={intl.get("baas.entidades.create.stepDesc.activityAndUsage")}
            />
            <SectionHeading>{intl.get("baas.entidades.create.section.processingActivities")}</SectionHeading>
            <IndustrySelectField value={form.industry} onChange={handleIndustryChange} required />
            <IndustryRiskBadge riskLevel={industryRequirements.riskLevel} industry={form.industry} />
            <MultiCheckboxField
              label={intl.get("baas.entidades.create.processingActivities")}
              options={BAAS_PURPOSE_OF_ACCOUNT}
              values={form.purposeOfAccount}
              onChange={(values) => syncProcessingRisk({ purposeOfAccount: values })}
              labelKeyPrefix="baas.entidades.create.options.purposeOfAccount"
              required={fieldRequired("purposeOfAccount")}
            />
            <MultiCheckboxField
              label={intl.get("baas.entidades.create.expectedPaymentRails")}
              options={availablePaymentRails}
              values={form.expectedPaymentRails}
              onChange={(values) => syncProcessingRisk({ expectedPaymentRails: values })}
              labelKeyPrefix="baas.entidades.create.options.paymentRails"
              required={fieldRequired("expectedPaymentRails")}
            />
            {processingRisk.isHighRisk ? (
              <EntityHighRiskBanner reasons={processingRisk.reasons} />
            ) : null}

            <SectionHeading>{intl.get("baas.entidades.create.section.businessProfile")}</SectionHeading>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label={intl.get("baas.entidades.create.website")}
                required={fieldRequired("website")}
                optional={!fieldRequired("website")}
              >
                <input
                  type="url"
                  className={inputClass}
                  value={form.website}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, website: event.target.value }))
                  }
                  placeholder="https://"
                />
              </Field>
            </div>
            <Field
              label={intl.get("baas.entidades.create.description")}
              required={fieldRequired("description")}
              optional={!fieldRequired("description")}
            >
              <textarea
                className={textareaClass}
                value={form.description}
                onChange={(event) =>
                  setForm((current) => ({ ...current, description: event.target.value }))
                }
              />
            </Field>

            <SectionHeading>{intl.get("baas.entidades.create.section.operations")}</SectionHeading>
            <MultiCheckboxField
              label={intl.get("baas.entidades.create.countriesOfOperation")}
              options={BAAS_COUNTRY_CODES}
              values={form.countriesOfOperation}
              onChange={(values) => syncProcessingRisk({ countriesOfOperation: values })}
              labelKeyPrefix="baas.entidades.create.country"
              required
            />
            <Field label={intl.get("baas.entidades.create.accountUsage")} required>
              <textarea
                className={textareaClass}
                value={form.accountUsage}
                onChange={(event) =>
                  setForm((current) => ({ ...current, accountUsage: event.target.value }))
                }
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label={intl.get("baas.entidades.create.paymentVolumes")}
                required={fieldRequired("paymentVolumes")}
                optional={!fieldRequired("paymentVolumes")}
              >
                <select
                  className={inputClass}
                  value={form.paymentVolumes}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, paymentVolumes: event.target.value }))
                  }
                >
                  {BAAS_PAYMENT_VOLUMES.map((volume) => (
                    <option key={volume} value={volume}>
                      {enumLabel("paymentVolumes", volume)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label={intl.get("baas.entidades.create.endCustomerType")}
                required={fieldRequired("endCustomerType")}
                optional={!fieldRequired("endCustomerType")}
              >
                <select
                  className={inputClass}
                  value={form.endCustomerType}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, endCustomerType: event.target.value }))
                  }
                >
                  {BAAS_END_CUSTOMER_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {enumLabel("endCustomerType", type)}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <Field
              label={intl.get("baas.entidades.create.sourceOfFunds")}
              required={fieldRequired("sourceOfFunds")}
              optional={!fieldRequired("sourceOfFunds")}
            >
              <textarea
                className={textareaClass}
                value={form.sourceOfFunds}
                onChange={(event) =>
                  setForm((current) => ({ ...current, sourceOfFunds: event.target.value }))
                }
              />
            </Field>
            <Field
              label={intl.get("baas.entidades.create.expectedUseOfFunds")}
              required={fieldRequired("expectedUseOfFunds")}
              optional={!fieldRequired("expectedUseOfFunds")}
            >
              <textarea
                className={textareaClass}
                value={form.expectedUseOfFunds}
                onChange={(event) =>
                  setForm((current) => ({ ...current, expectedUseOfFunds: event.target.value }))
                }
              />
            </Field>
            <CheckboxField
              label={intl.get("baas.entidades.create.makesInternationalPayments")}
              checked={form.makesInternationalPayments}
              onChange={(event) =>
                syncProcessingRisk({ makesInternationalPayments: event.target.checked })
              }
            />
          </>
        );

      case "ownerPersonal":
        return (
          <>
            <StepHeader
              title={intl.get("baas.entidades.create.step.ownerPersonal")}
              description={intl.get("baas.entidades.create.stepDesc.ownerPersonal")}
            />
            {renderOwnerSelector()}
            {ownerLinked ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {intl.get("baas.entidades.create.existingPersonReadOnlyHint")}
              </p>
            ) : null}
            <SectionHeading>{intl.get("baas.entidades.create.section.ownerIdentity")}</SectionHeading>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={intl.get("baas.entidades.create.owner.firstName")} required={!ownerLinked}>
                <input
                  type="text"
                  className={inputClass}
                  value={owner.firstName}
                  onChange={(event) => updateOwner({ firstName: event.target.value })}
                  required={!ownerLinked}
                  disabled={linkedOwnerFieldsLocked}
                  readOnly={linkedOwnerFieldsLocked}
                />
              </Field>
              <Field label={intl.get("baas.entidades.create.owner.middleName")} optional>
                <input
                  type="text"
                  className={inputClass}
                  value={owner.middleName}
                  onChange={(event) => updateOwner({ middleName: event.target.value })}
                  disabled={linkedOwnerFieldsLocked}
                  readOnly={linkedOwnerFieldsLocked}
                />
              </Field>
              <Field label={intl.get("baas.entidades.create.owner.lastName")} required={!ownerLinked}>
                <input
                  type="text"
                  className={inputClass}
                  value={owner.lastName}
                  onChange={(event) => updateOwner({ lastName: event.target.value })}
                  required={!ownerLinked}
                  disabled={linkedOwnerFieldsLocked}
                  readOnly={linkedOwnerFieldsLocked}
                />
              </Field>
              {!ownerLinked && isBrCountry(owner.address?.countryCode || form.countryOfIncorporation) ? (
                <Field
                  label={intl.get("baas.entidades.create.identity.nationalId") || "CPF"}
                  required
                >
                  <input
                    type="text"
                    className={inputClass}
                    value={owner.nationalId?.number || ""}
                    onChange={(event) =>
                      updateOwnerDoc("nationalId", {
                        number: event.target.value.replace(/\D/g, "").slice(0, 11),
                        countryCode: owner.nationalId?.countryCode || "BR",
                      })
                    }
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="00000000000"
                  />
                </Field>
              ) : null}
              <Field
                label={intl.get("baas.entidades.create.owner.dateOfBirth")}
                required={!ownerLinked && ownerFieldRequired("dateOfBirth")}
                optional={ownerLinked || !ownerFieldRequired("dateOfBirth")}
              >
                <input
                  type="date"
                  className={inputClass}
                  value={owner.dateOfBirth}
                  onChange={(event) => updateOwner({ dateOfBirth: event.target.value })}
                  disabled={linkedOwnerFieldsLocked}
                  readOnly={linkedOwnerFieldsLocked}
                />
              </Field>
              <Field
                label={intl.get("baas.entidades.create.owner.motherName")}
                required={
                  !ownerLinked &&
                  isBrCountry(owner.address?.countryCode || form.countryOfIncorporation)
                }
                optional={
                  ownerLinked ||
                  !isBrCountry(owner.address?.countryCode || form.countryOfIncorporation)
                }
              >
                <input
                  type="text"
                  className={inputClass}
                  value={owner.motherName ?? ""}
                  onChange={(event) => updateOwner({ motherName: event.target.value })}
                  disabled={linkedOwnerFieldsLocked}
                  readOnly={linkedOwnerFieldsLocked}
                />
              </Field>
              <Field label={intl.get("baas.entidades.create.owner.email")} required={!ownerLinked}>
                <input
                  type="email"
                  className={inputClass}
                  value={owner.email}
                  onChange={(event) => updateOwner({ email: event.target.value })}
                  required={!ownerLinked}
                  disabled={linkedOwnerFieldsLocked}
                  readOnly={linkedOwnerFieldsLocked}
                />
              </Field>
            </div>
            {!ownerLinked ? (
              <PhonePartsFields
                value={owner.phone}
                onChange={(patch) =>
                  updateOwner({ phone: { ...(owner.phone || {}), ...patch } })
                }
                dialOptions={phoneDialOptions}
                required={ownerFieldRequired("phone")}
                optional={!ownerFieldRequired("phone")}
                className="mt-4"
              />
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              {visible.ssn && !ownerLinked ? (
                <Field label={intl.get("baas.entidades.create.owner.ssn")} optional>
                  <input
                    type="text"
                    className={inputClass}
                    value={owner.ssn}
                    onChange={(event) => updateOwner({ ssn: event.target.value })}
                  />
                </Field>
              ) : null}
              {!ownerLinked ? (
                <CountrySelectField
                  label={intl.get("baas.entidades.create.owner.countryOfCitizenship")}
                  value={owner.countryOfCitizenship}
                  onChange={(value) => updateOwner({ countryOfCitizenship: value })}
                  countryOptions={countryOptions}
                  required={ownerFieldRequired("countryOfCitizenship")}
                  optional={!ownerFieldRequired("countryOfCitizenship")}
                />
              ) : null}
            </div>
            {!visible.ssn && !ownerLinked ? (
              <ConditionalHelper messageKey="baas.entidades.create.ssnUsOnly" />
            ) : null}

            {!ownerLinked ? (
              <>
                <SectionHeading>
                  {intl.get("baas.entidades.create.section.ownerEmployment")}
                </SectionHeading>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label={intl.get("baas.entidades.create.owner.employmentStatus")} optional>
                    <select
                      className={inputClass}
                      value={owner.employmentStatus}
                      onChange={(event) => updateOwner({ employmentStatus: event.target.value })}
                    >
                      {BAAS_EMPLOYMENT_STATUSES.map((status) => (
                        <option key={status} value={status}>
                          {enumLabel("employmentStatus", status)}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field
                    label={intl.get("baas.entidades.create.owner.occupation")}
                    required={ownerFieldRequired("occupation")}
                    optional={!ownerFieldRequired("occupation")}
                  >
                    <input
                      type="text"
                      className={inputClass}
                      value={owner.occupation}
                      onChange={(event) => updateOwner({ occupation: event.target.value })}
                    />
                  </Field>
                  <Field label={intl.get("baas.entidades.create.owner.role")} optional>
                    <select
                      className={inputClass}
                      value={owner.role}
                      onChange={(event) => updateOwner({ role: event.target.value })}
                    >
                      {BAAS_OWNER_ROLES.map((role) => (
                        <option key={role} value={role}>
                          {enumLabel("ownerRole", role)}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field
                    label={intl.get("baas.entidades.create.owner.jobTitle")}
                    required={ownerFieldRequired("jobTitle")}
                    optional={!ownerFieldRequired("jobTitle")}
                  >
                    <input
                      type="text"
                      className={inputClass}
                      value={owner.jobTitle}
                      onChange={(event) => updateOwner({ jobTitle: event.target.value })}
                    />
                  </Field>
                  <Field label={intl.get("baas.entidades.create.owner.income")} optional>
                    <input
                      type="number"
                      min="0"
                      className={inputClass}
                      value={owner.incomeAmount}
                      onChange={(event) => updateOwner({ incomeAmount: event.target.value })}
                    />
                  </Field>
                  <Field
                    label={intl.get("baas.entidades.create.owner.pepStatus")}
                    required={ownerFieldRequired("pepStatus")}
                    optional={!ownerFieldRequired("pepStatus")}
                  >
                    <select
                      className={inputClass}
                      value={owner.pepStatus}
                      onChange={(event) => updateOwner({ pepStatus: event.target.value })}
                    >
                      {BAAS_PEP_STATUSES.map((status) => (
                        <option key={status} value={status}>
                          {enumLabel("pepStatus", status)}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
                <MultiCheckboxField
                  label={intl.get("baas.entidades.create.owner.sourceOfIncome")}
                  options={BAAS_SOURCE_OF_INCOME}
                  values={owner.sourceOfIncome}
                  onChange={(values) => updateOwner({ sourceOfIncome: values })}
                  labelKeyPrefix="baas.entidades.create.options.sourceOfIncome"
                />
                <CheckboxField
                  label={intl.get("baas.entidades.create.owner.isHighRisk")}
                  checked={owner.isHighRisk}
                  onChange={(event) => updateOwner({ isHighRisk: event.target.checked })}
                />
                <OwnerParticipationFlags
                  owner={owner}
                  onChange={updateOwner}
                  locked={participationFlagsLocked}
                  lastBeneficialOwner={lastBeneficialOwner}
                />
              </>
            ) : (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label={intl.get("baas.entidades.create.owner.role")} optional>
                    <select
                      className={inputClass}
                      value={owner.role}
                      onChange={(event) => updateOwner({ role: event.target.value })}
                      disabled={ownerAccountLocked}
                    >
                      {BAAS_OWNER_ROLES.map((role) => (
                        <option key={role} value={role}>
                          {enumLabel("ownerRole", role)}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
                <OwnerParticipationFlags
                  owner={owner}
                  onChange={updateOwner}
                  locked={participationFlagsLocked}
                  lastBeneficialOwner={lastBeneficialOwner}
                />
              </>
            )}
          </>
        );

      case "ownerAddress":
        return (
          <>
            <StepHeader
              title={intl.get("baas.entidades.create.step.ownerAddress")}
              description={intl.get("baas.entidades.create.stepDesc.ownerAddress")}
            />
            {renderOwnerSelector()}
            {ownerLinked ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {intl.get("baas.entidades.create.existingPersonAddressOnPerson")}
              </p>
            ) : isBrCountry(owner.address?.countryCode) ? (
              <BrAddressFieldsGrid
                address={owner.address}
                onChange={updateOwnerAddress}
                required
                labelPrefix="baas.entidades.create.address"
                countryOptions={countryOptions}
              />
            ) : (
              <AddressFieldsGrid
                address={owner.address}
                onChange={updateOwnerAddress}
                required
                countryOptions={countryOptions}
              />
            )}
          </>
        );

      case "ownerIdentity":
        return (
          <>
            <StepHeader
              title={intl.get("baas.entidades.create.step.ownerIdentity")}
              description={
                ownerLinked
                  ? intl.get("baas.entidades.create.stepDesc.ownerIdentityLinked")
                  : intl.get("baas.entidades.create.stepDesc.ownerIdentity")
              }
            />
            {renderOwnerSelector()}
            {ownerLinked ? (
              <div className="pointer-events-auto rounded-xl border border-dashed border-gray-200 p-4 dark:border-brand-dark-border">
                <p className="text-sm text-brand-dark dark:text-gray-100">
                  {intl.get("baas.entidades.create.partnerDocsOnPersonTitle")}
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {intl.get("baas.entidades.create.partnerDocsOnPersonHint")}
                </p>
                <PersonConsoleLink persId={owner.persId} className="mt-3 text-sm">
                  {intl.get("baas.entidades.create.openPersonDocuments")}
                </PersonConsoleLink>
              </div>
            ) : (
              <>
                {ownerFieldError ? (
                  <p
                    role="alert"
                    className="rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-400/50 dark:bg-red-950/40 dark:text-red-100"
                  >
                    {ownerFieldError}
                  </p>
                ) : null}
                {isStepOptionalForIndustry("ownerIdentity", industryRequirements) ? (
                  <OptionalSectionNotice />
                ) : null}
                <CollapsibleSection
                  title={intl.get("baas.entidades.create.identity.passport")}
                  optional
                  defaultOpen={hasIdentityDocData(owner.passport) || Boolean(ownerFieldError)}
                >
                  <IdentityDocFields
                    doc={owner.passport}
                    onChange={(patch) => updateOwnerDoc("passport", patch)}
                    compact
                    countryOptions={countryOptions}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title={intl.get("baas.entidades.create.identity.driversLicense")}
                  optional
                  defaultOpen={Boolean(owner.driversLicense?.number?.trim()) || Boolean(ownerFieldError)}
                >
                  <IdentityDocFields
                    doc={owner.driversLicense}
                    onChange={(patch) => updateOwnerDoc("driversLicense", patch)}
                    compact
                    countryOptions={countryOptions}
                    forcedCountryCode={isBrCountry(owner.nationalId?.countryCode) ? "BR" : null}
                  />
                </CollapsibleSection>
                <CollapsibleSection
                  title={intl.get("baas.entidades.create.identity.nationalId")}
                  optional
                  defaultOpen={Boolean(owner.nationalId?.number?.trim()) || Boolean(ownerFieldError)}
                >
                  <IdentityDocFields
                    doc={owner.nationalId}
                    onChange={(patch) => updateOwnerDoc("nationalId", patch)}
                    compact
                    countryOptions={countryOptions}
                  />
                </CollapsibleSection>
              </>
            )}
          </>
        );

      case "ownerAccount":
        return (
          <>
            <StepHeader
              title={intl.get("baas.entidades.create.step.ownerAccount")}
              description={intl.get("baas.entidades.create.stepDesc.ownerAccount")}
            />
            {renderOwnerSelector()}
            {isStepOptionalForIndustry("ownerAccount", industryRequirements) ? (
              <OptionalSectionNotice />
            ) : null}
            <MultiCheckboxField
              label={intl.get("baas.entidades.create.owner.purposeOfAccount")}
              options={BAAS_PURPOSE_OF_ACCOUNT}
              values={owner.purposeOfAccount}
              onChange={(values) => updateOwner({ purposeOfAccount: values })}
              labelKeyPrefix="baas.entidades.create.options.purposeOfAccount"
              required={ownerFieldRequired("purposeOfAccount")}
              disabled={ownerAccountLocked}
            />
            <Field
              label={intl.get("baas.entidades.create.owner.ownership")}
              required={ownerFieldRequired("ownershipPercentage")}
              optional={!ownerFieldRequired("ownershipPercentage")}
            >
              <input
                type="number"
                min="0"
                max="100"
                className={inputClass}
                value={owner.ownershipPercentage}
                onChange={(event) => updateOwner({ ownershipPercentage: event.target.value })}
                disabled={ownerAccountLocked}
                readOnly={ownerAccountLocked}
              />
            </Field>
            <CheckboxField
              label={intl.get("baas.entidades.create.owner.makesInternationalPayments")}
              checked={owner.makesInternationalPayments}
              onChange={(event) =>
                updateOwner({ makesInternationalPayments: event.target.checked })
              }
              disabled={ownerAccountLocked}
            />
            <OwnerParticipationFlags
              owner={owner}
              onChange={updateOwner}
              locked={participationFlagsLocked}
              lastBeneficialOwner={lastBeneficialOwner}
            />
          </>
        );

      case "review":
        return (
          <div className={reviewClassName}>
            <StepHeader
              title={intl.get("baas.entidades.create.step.review")}
              description={intl.get("baas.entidades.create.stepDesc.review")}
            />

            <ReviewSection title={intl.get("baas.entidades.create.step.document")}>
              <p>
                {intl.get(`baas.entidades.create.country.${form.countryOfIncorporation}`) ||
                  form.countryOfIncorporation}
              </p>
              {visible.ein ? (
                <p>
                  {intl.get("baas.entidades.create.ein")}: {form.ein || "—"}
                  {form.einPending ? ` (${intl.get("baas.entidades.create.einPending")})` : ""}
                </p>
              ) : null}
              {visible.registrationId ? (
                <p>
                  {isBrCountry(form.countryOfIncorporation)
                    ? intl.get("baas.entidades.create.cnpjLabel")
                    : intl.get("baas.entidades.create.registrationNumber")}
                  : {form.registrationId.number || "—"} · {form.registrationId.countryCode || "—"}
                </p>
              ) : null}
            </ReviewSection>

            <ReviewSection title={intl.get("baas.entidades.create.step.companyIdentity")}>
              <p className="font-medium text-brand-dark dark:text-gray-100">{form.businessName}</p>
              {form.dbaName ? <p>{form.dbaName}</p> : null}
              {form.email ? <p>{form.email}</p> : null}
              <p>{formatPhonePartsForDisplay(form.phone)}</p>
            </ReviewSection>

            <ReviewSection title={intl.get("baas.entidades.create.step.incorporation")}>
              <p>
                {form.dateOfIncorporation}
                {form.yearOfIncorporation ? ` · ${form.yearOfIncorporation}` : ""}
              </p>
            </ReviewSection>

            <ReviewSection title={intl.get("baas.entidades.create.step.countryAndType")}>
              <p>
                {intl.get(`baas.entidades.create.country.${form.countryOfIncorporation}`) ||
                  form.countryOfIncorporation}
                {form.stateOfIncorporation ? ` / ${form.stateOfIncorporation}` : ""}
              </p>
              <p>{legalTypeLabel(form.legalType)}</p>
            </ReviewSection>

            <ReviewSection title={intl.get("baas.entidades.create.step.businessAddress")}>
              <p>{formatAddress(form.address)}</p>
            </ReviewSection>

            <ReviewSection title={intl.get("baas.entidades.create.step.activityAndUsage")}>
              <p>{getIndustryOptionLabel(form.industry)}</p>
              <IndustryRiskBadge riskLevel={industryRequirements.riskLevel} industry={form.industry} />
              {form.purposeOfAccount?.length ? (
                <p>
                  {form.purposeOfAccount
                    .map((item) => enumLabel("purposeOfAccount", item))
                    .join(", ")}
                </p>
              ) : null}
              {form.expectedPaymentRails?.length ? (
                <p>
                  {form.expectedPaymentRails
                    .map((item) => enumLabel("paymentRails", item))
                    .join(", ")}
                </p>
              ) : null}
              {processingRisk.isHighRisk ? (
                <EntityHighRiskBanner reasons={processingRisk.reasons} compact />
              ) : null}
              {form.website ? <p>{form.website}</p> : null}
              {form.description ? <p>{form.description}</p> : null}
              <p>{form.countriesOfOperation.join(", ") || "—"}</p>
              <p>{form.accountUsage || "—"}</p>
              <p>
                {form.sourceOfFunds
                  ? form.sourceOfFunds
                  : intl.get("baas.entidades.create.reviewNotApplicable")}
              </p>
              <p>
                {form.paymentVolumes
                  ? enumLabel("paymentVolumes", form.paymentVolumes)
                  : intl.get("baas.entidades.create.reviewNotApplicable")}
              </p>
            </ReviewSection>

            <ReviewSection title={intl.get("baas.entidades.create.section.beneficialOwners")}>
              <OwnershipTotalBanner owners={form.beneficialOwners} />
              <div className="mt-3 space-y-3">
                {form.beneficialOwners.map((item, index) => (
                  <OwnerReviewCard
                    key={`review-owner-${index}`}
                    owner={item}
                    index={index}
                    visible={getVisibleFields(form, item)}
                    enumLabel={enumLabel}
                    industryRequirements={industryRequirements}
                  />
                ))}
              </div>
            </ReviewSection>

            <p className="text-xs text-gray-500">{intl.get("baas.entidades.create.reviewHint")}</p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <form
      onSubmit={isViewOnly ? (event) => event.preventDefault() : handleSubmit}
      className={`baas-entidade-create-form relative min-w-0${isCompact ? " baas-entidade-create-form--compact" : ""}${showHeader ? " p-6" : ""}${isViewOnly ? " baas-entidade-create-form--readonly" : ""}`}
      data-debug-expanded={debugExpanded ? "true" : "false"}
      data-readonly={isViewOnly ? "true" : "false"}
      data-edit-mode={isEditableDetail ? "true" : "false"}
    >
      {showHeader ? (
        <div className="mb-5 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-gold/10 text-brand-gold">
            <TbBuildingBank size={20} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-brand-dark dark:text-gray-100">
              {intl.get("baas.entidades.create.title")}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {intl.get("baas.entidades.create.subtitle")}
            </p>
          </div>
        </div>
      ) : null}

      <div className="mb-5">
        <WizardStepTabs
          steps={BAAS_ENTIDADE_CREATE_STEPS}
          activeIndex={stepIndex}
          onStepClick={handleStepClick}
          form={form}
          visible={visible}
          industryRequirements={industryRequirements}
          instanceId={instanceId}
        />
      </div>

      <div
        className={`min-w-0 space-y-4 ${isViewOnly ? "pointer-events-none opacity-95" : ""}`}
        aria-readonly={isViewOnly ? "true" : undefined}
        onBeforeInput={isViewOnly ? (event) => event.preventDefault() : undefined}
        onKeyDown={
          isViewOnly
            ? (event) => {
                const target = event.target;
                if (
                  target instanceof HTMLInputElement ||
                  target instanceof HTMLTextAreaElement ||
                  target instanceof HTMLSelectElement
                ) {
                  event.preventDefault();
                }
              }
            : undefined
        }
      >
        {renderStepContent()}
      </div>

      {/* Detail + create: documents panel only on Identificação (companyIdentity) tab — not on every wizard step */}
      {entityId && !hideDocumentsPanel && currentStep === "companyIdentity" ? (
        <BaasEntidadeDocumentsPanel
          embedded
          documents={Array.isArray(documents) ? documents : []}
          instance={activeInstance}
          entityId={entityId}
          entityType="pj"
          uploadEnabled={Boolean(documentsUploadEnabled)}
          allowSkipKyc={allowSkipKyc ?? Boolean(documentsUploadEnabled)}
          canEditMeta={isEditableDetail}
          onUploaded={onDocumentsUploaded}
          onDocumentUpdated={onDocumentUpdated}
          verificationEntity={verificationEntity}
          allowPaySmartImageSync={!isCreateFlow}
        />
      ) : null}

      <BaasExistingPersonPickerModal
        open={personPickerOpen && canManageOwners}
        instance={activeInstance}
        excludePersIds={usedOwnerPersIds}
        onClose={() => setPersonPickerOpen(false)}
        onSelect={handleSelectExistingPerson}
      />

      {validationIssues.length > 0 && !isViewOnly ? (
        <div className={formAlertClass} role="alert">
          <p className="font-semibold text-red-900 dark:text-red-50">
            {intl.get("baas.entidades.create.requiredFieldsTitle")}
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-red-800 dark:text-red-100">
            {validationIssues.map((issue) => (
              <li key={`${issue.step}-${issue.errorKey}`}>
                {intl.get(issue.errorKey ?? "baas.entidades.create.incompleteRequiredFields")}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {error && !isViewOnly ? (
        <div className={formAlertClass} role="alert">
          <p className="whitespace-pre-wrap">{error}</p>
          {existingEntityId && lookupNoticeTone === "warn" ? (
            <button
              type="button"
              className="btn-outline mt-3"
              onClick={() => history.push(buildBusinessEntityDetailPath(existingEntityId))}
            >
              {intl.get("baas.entidades.create.lookup.openEntity")}
            </button>
          ) : null}
        </div>
      ) : null}
      {saveSuccess ? <p className="mt-4 text-sm font-medium text-emerald-600">{saveSuccess}</p> : null}

      <div
        className={`flex justify-end gap-2 ${isCompact ? "mt-6" : "mt-8 border-t border-gray-100 pt-6 dark:border-brand-dark-border"}`}
      >
        {onCancel && !isViewOnly && !isEditableDetail ? (
          <button type="button" className="btn-secondary" onClick={handleCancel} disabled={submitting}>
            {cancelLabel ?? intl.get("common.cancel")}
          </button>
        ) : null}
        {stepIndex > 0 ? (
          <button type="button" className="btn-secondary" onClick={handleBack} disabled={submitting || lookingUp}>
            {intl.get("baas.entidades.create.back")}
          </button>
        ) : null}
        {isViewOnly ? (
          !isLastStep ? (
            <button type="button" className="btn-primary" onClick={handleNext}>
              {intl.get("baas.entidades.create.next")}
            </button>
          ) : null
        ) : isEditableDetail ? (
          <>
            {!isLastStep ? (
              <button
                key="wizard-next"
                type="button"
                className="btn-outline"
                onClick={handleNext}
                disabled={submitting || lookingUp}
              >
                {intl.get("baas.entidades.create.next")}
              </button>
            ) : null}
            <button
              key="wizard-save"
              type="button"
              className="btn-primary"
              disabled={submitting}
              onClick={handleSubmit}
            >
              {submitting
                ? intl.get("baas.entidades.detail.saving")
                : intl.get("baas.entidades.detail.saveChanges")}
            </button>
          </>
        ) : isLastStep ? (
          <button
            key="wizard-submit"
            type="button"
            className="btn-primary"
            disabled={submitting || Boolean(existingEntityId)}
            onClick={handleSubmit}
          >
            {submitting
              ? intl.get("baas.entidades.create.submitting")
              : intl.get("baas.entidades.create.submit")}
          </button>
        ) : (
          <button
            key="wizard-next"
            type="button"
            className="btn-primary"
            onClick={handleNext}
            disabled={
              submitting ||
              lookingUp ||
              Boolean(existingEntityId && currentStep === "document") ||
              (isCreateFlow && currentStep === "document" && !documentContinueEnabled)
            }
          >
            {lookingUp
              ? intl.get("baas.entidades.create.lookup.lookingUp")
              : currentStep === "document"
                ? intl.get("baas.entidades.create.lookupAndContinue")
                : intl.get("baas.entidades.create.next")}
          </button>
        )}
      </div>

      {!readOnly && !editMode ? (
        <DebugConsoleFooter
          sticky={!isCompact}
          expanded={debugExpanded}
          onToggle={() => setDebugExpanded((current) => !current)}
          jsonText={jsonText}
          onJsonChange={(value) => {
            setJsonText(value);
            setDebugError("");
          }}
          onSyncFromForm={handleSyncJsonFromForm}
          onFormatJson={handleFormatJson}
          onRestoreTemplate={handleRestoreTemplate}
          onDebugSubmit={handleDebugSubmit}
          submitting={submitting}
          error={debugError}
        />
      ) : null}
    </form>
  );
};
